<script setup lang="ts">
import { computed } from 'vue';
import { DocumentIcon, PhotoIcon } from '@heroicons/vue/24/outline';
import type { CaseDocument } from './useCaseDocuments';

const props = withDefaults(
  defineProps<{
    documents: CaseDocument[];
    unavailableReason: string | null;
    // 'panel' docks under the desktop tool pane (bordered, shows the `D`
    // keycap chip); 'sheet' is the mobile bottom-sheet tab (wider,
    // snapping cards, no border, no keycap since shortcuts are desktop-only).
    variant?: 'panel' | 'sheet';
    kbEnabled?: boolean;
  }>(),
  { variant: 'panel', kbEnabled: false }
);

const emit = defineEmits<{ open: [fileId: string] }>();

function isImage(doc: CaseDocument): boolean {
  return doc.state === 'ready' && !!doc.mimeType?.startsWith('image/');
}

function isOpenablePdf(doc: CaseDocument): boolean {
  return doc.state === 'ready' && !isImage(doc);
}

const cardClass = computed(() =>
  props.variant === 'sheet'
    ? 'flex-none w-[138px] snap-start overflow-hidden rounded-[10px] border border-gray-200 bg-white text-left'
    : 'flex-none w-[104px] overflow-hidden rounded-[9px] border border-gray-200 bg-white text-left hover:border-gray-300'
);
const thumbClass = computed(() =>
  props.variant === 'sheet'
    ? 'grid h-24 w-full place-items-center overflow-hidden bg-gray-50'
    : 'grid h-[66px] w-full place-items-center overflow-hidden bg-gray-50'
);
</script>

<template>
  <div
    :class="variant === 'panel' ? 'flex-none border-t border-gray-200 p-3' : 'flex flex-col'"
    data-test="case-documents-panel"
  >
    <div class="mb-2 flex items-center gap-1.5">
      <span class="text-[11px] font-semibold tracking-wide text-gray-400 uppercase">Documents</span>
      <span
        v-if="documents.length"
        class="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500"
      >
        {{ documents.length }}
      </span>
      <span class="flex-1" />
      <span
        v-if="variant === 'panel' && kbEnabled"
        data-test="documents-kbd"
        class="rounded border border-gray-300 px-1.5 py-0.5 text-[10px] text-gray-500"
        >D</span
      >
    </div>

    <p
      v-if="unavailableReason"
      data-test="documents-unavailable"
      class="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[12px] text-amber-800"
    >
      {{ unavailableReason }}
    </p>
    <p v-else-if="!documents.length" class="text-[12.5px] text-gray-400">
      No documents on file for this review.
    </p>

    <div
      v-else
      :class="[
        'flex overflow-x-auto',
        variant === 'sheet' ? 'snap-x snap-mandatory gap-2.5' : 'gap-2',
      ]"
      data-test="documents-strip"
    >
      <button
        v-for="doc in documents"
        :key="doc.fileId"
        type="button"
        data-test="document-tile"
        :class="cardClass"
        :disabled="doc.state !== 'ready'"
        @click="doc.state === 'ready' && emit('open', doc.fileId)"
      >
        <div :class="thumbClass">
          <img
            v-if="isImage(doc)"
            :src="doc.objectUrl"
            :alt="doc.label"
            class="h-full w-full object-cover"
          />
          <div
            v-else-if="doc.state === 'loading'"
            class="h-4 w-4 animate-pulse rounded-full bg-gray-300"
            :aria-label="`${doc.label} loading`"
          />
          <span
            v-else-if="isOpenablePdf(doc)"
            data-test="document-tile-pdf"
            class="flex flex-col items-center gap-1 text-gray-400"
          >
            <DocumentIcon class="h-[18px] w-[18px]" />
            <span class="text-[8.5px] font-semibold tracking-wide">PDF</span>
          </span>
          <PhotoIcon v-else class="h-6 w-6 text-gray-300" :aria-label="`${doc.label} failed`" />
        </div>
        <span
          class="block truncate border-t border-gray-100 px-2 py-1 text-[10.5px] font-medium text-gray-600"
          >{{ doc.label }}</span
        >
      </button>
    </div>
  </div>
</template>
